import { apiClientV2 } from '@/api/clientV2';

// Interface for analyzed file with AI extraction and matching
export interface AnalyzeResult {
  filename: string;
  paymentDate: string | null;
  amount: number | null;
  matched: boolean;
  installment: InstallmentMatch | null;
  error?: string;
}

export interface InstallmentMatch {
  id: string;
  installmentNumber: number;
  amount: string;
  dueDate: string;
  paymentMethod: string | null;
  entry: {
    id: string;
    description: string;
    documentNumber: string | null;
    competenceDate: string;
    totalCost: string;
    customerId: string;
  };
}

export interface ConfirmationInput {
  filename: string;
  installmentId?: string;
  customerId?: string;
  paymentDate: string;
  amount?: string;
  notes?: string;
}

export interface ConfirmResult {
  created: Array<{
    filename: string;
    installmentId: string;
    receiptId: string;
  }>;
  errors: Array<{
    filename: string;
    error: string;
  }>;
}

class BatchImportService {
  /**
   * Upload individual files and create async extraction job
   */
  async uploadFiles(
    files: File[],
    customerId?: string
  ): Promise<{
    success: boolean;
    jobId?: string;
    error?: string;
  }> {
    try {
      const formData = new FormData();
      if (customerId) {
        formData.append('customerId', customerId);
      }

      files.forEach((file, i) => {
        formData.append(`file_${i}`, file);
      });

      const { data } = await apiClientV2.post<{ jobId: string; status: string }>(
        '/receipts/batch/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!data.jobId) {
        return { success: false, error: 'Erro ao criar job de análise' };
      }

      return { success: true, jobId: data.jobId };
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message || 'Erro ao enviar arquivos',
      };
    }
  }

  /**
   * Get job status by ID
   */
  async getJobStatus(jobId: string): Promise<{
    success: boolean;
    data?: {
      id: string;
      status: string;
      progress: number;
      metadata: Record<string, unknown>;
      result: { results: AnalyzeResult[] } | null;
      error: string | null;
      customer?: {
        id: string;
        cnpj: string;
        legalName: string;
      };
    };
    error?: string;
  }> {
    try {
      const { data } = await apiClientV2.get<{
        id: string;
        status: string;
        progress: number;
        metadata: Record<string, unknown>;
        result: { results: AnalyzeResult[] } | null;
        error: string | null;
        customer?: {
          id: string;
          cnpj: string;
          legalName: string;
        };
      }>(`/receipts/batch/jobs/${jobId}`);

      return { success: true, data };
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Erro ao buscar status do job',
      };
    }
  }

  /**
   * Poll job status until completion
   */
  async pollJobStatus(
    jobId: string,
    onProgress?: (progress: number, metadata: Record<string, unknown>) => void
  ): Promise<{
    success: boolean;
    data?: AnalyzeResult[];
    error?: string;
  }> {
    const maxWaitTime = 5 * 60 * 1000; // 5 minutes
    const pollInterval = 2000; // 2 seconds
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      try {
        const { data } = await apiClientV2.get<{
          id: string;
          status: string;
          progress: number;
          metadata: Record<string, unknown>;
          result: { results: AnalyzeResult[] } | null;
          error: string | null;
        }>(`/receipts/batch/jobs/${jobId}`);

        // Notify progress
        if (onProgress) {
          onProgress(data.progress, data.metadata);
        }

        // Job completed successfully
        if (data.status === 'completed') {
          if (data.result && data.result.results) {
            return { success: true, data: data.result.results };
          }
          return { success: false, error: 'Job completado mas sem resultados' };
        }

        // Job failed
        if (data.status === 'failed') {
          return { success: false, error: data.error || 'Job falhou' };
        }

        // Still processing, wait and continue
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      } catch (error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        return {
          success: false,
          error: axiosError.response?.data?.error || 'Erro ao verificar status do job',
        };
      }
    }

    return { success: false, error: 'Timeout aguardando resultado do job' };
  }

  /**
   * Retry AI extraction for a single file from an existing job
   */
  async retryExtraction(
    jobId: string,
    filename: string
  ): Promise<{
    success: boolean;
    data?: AnalyzeResult;
    error?: string;
  }> {
    try {
      const { data } = await apiClientV2.post<AnalyzeResult>(
        `/receipts/batch/jobs/${jobId}/retry/${encodeURIComponent(filename)}`
      );

      return { success: true, data };
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message || 'Erro ao reprocessar arquivo',
      };
    }
  }

  /**
   * Confirm imports using job's stored files
   */
  async confirmImportsFromJob(
    jobId: string,
    confirmations: ConfirmationInput[]
  ): Promise<{
    success: boolean;
    data?: ConfirmResult;
    error?: string;
  }> {
    try {
      const { data } = await apiClientV2.post<ConfirmResult>(
        '/receipts/batch/confirm-from-job',
        {
          jobId,
          confirmations,
        }
      );

      return {
        success: true,
        data,
      };
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Erro ao confirmar importações',
      };
    }
  }
}

export const batchImportService = new BatchImportService();
